import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import type { NormalizedApiError } from '@/shared/api';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useActivatePublicationCatalog,
  useArchivePublicationCatalog,
  useCreatePublicationCatalog,
  usePausePublicationCatalog,
  usePublicationCatalogTypes,
  usePublicationCatalogs,
  useRepublishPublicationCatalog,
  useResumePublicationCatalog,
  useUpdatePublicationCatalog,
} from '../../hooks';
import { formatDateTime } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import {
  PUBLICATION_CATALOG_STATUS_LABELS,
  PUBLICATION_LIFECYCLE_KIND_LABELS,
  publicationCatalogStatusColor,
} from '../../model/options';
import type { PublicationCatalog } from '../../model/publicationTypes';
import {
  PublicationCatalogFormDialog,
  type PublicationCatalogFormValue,
} from './PublicationCatalogFormDialog';

type LifecycleAction = 'activate' | 'pause' | 'resume' | 'archive' | 'republish';

/**
 * A catalog's lifecycle-action enablement, matching exactly what each endpoint documents as its
 * valid source status (activate: DRAFT only; pause: ACTIVE + PERMANENT only; resume: PAUSED only;
 * archive: any non-ARCHIVED status). Republish is not restricted by status in the contract at all
 * — enabling it whenever the catalog isn't ARCHIVED is this UI's own judgment call, not a
 * documented backend rule (flagged in user-story-merchant-portal-035's Notes).
 */
function isLifecycleActionEnabled(catalog: PublicationCatalog, action: LifecycleAction): boolean {
  switch (action) {
    case 'activate':
      return catalog.status === 'DRAFT';
    case 'pause':
      return catalog.status === 'ACTIVE' && catalog.lifecycleKind === 'PERMANENT';
    case 'resume':
      return catalog.status === 'PAUSED';
    case 'archive':
      return catalog.status !== 'ARCHIVED';
    case 'republish':
      return catalog.status !== 'ARCHIVED';
    default:
      return false;
  }
}

/** Publication catalogs tab — create/edit, and the activate/pause/resume/archive/republish lifecycle. */
export function PublicationCatalogsPanel({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const catalogsQuery = usePublicationCatalogs();
  const catalogTypesQuery = usePublicationCatalogTypes();
  const create = useCreatePublicationCatalog();
  const update = useUpdatePublicationCatalog();
  const activate = useActivatePublicationCatalog();
  const pause = usePausePublicationCatalog();
  const resume = useResumePublicationCatalog();
  const archive = useArchivePublicationCatalog();
  const republish = useRepublishPublicationCatalog();
  const { toast, success, error: notifyError, clear } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PublicationCatalog | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; catalog: PublicationCatalog } | null>(
    null,
  );
  const [confirmTarget, setConfirmTarget] = useState<{
    action: LifecycleAction;
    catalog: PublicationCatalog;
  } | null>(null);

  if (catalogsQuery.isLoading) {
    return <LoadingState label={t('publicationCatalogsPanel.loading')} />;
  }
  if (catalogsQuery.isError) {
    return (
      <ErrorState message={catalogsQuery.error?.message} onRetry={() => void catalogsQuery.refetch()} />
    );
  }

  const catalogs = catalogsQuery.data ?? [];
  const catalogTypes = catalogTypesQuery.data ?? [];
  const catalogTypeLabel = (code: string) =>
    catalogTypes.find((ct) => ct.code === code)?.displayName ?? code;

  const lifecyclePending =
    activate.isPending || pause.isPending || resume.isPending || archive.isPending || republish.isPending;

  const handleError = (error: NormalizedApiError) => notifyError(error.message);

  const submitForm = (value: PublicationCatalogFormValue) => {
    if (editing) {
      update.mutate(
        { catalogId: editing.id, body: { name: value.name, description: value.description || undefined } },
        {
          onSuccess: () => {
            success(t('publicationCatalogsPanel.saved'));
            setFormOpen(false);
            setEditing(null);
          },
          onError: handleError,
        },
      );
      return;
    }
    create.mutate(
      {
        code: value.code.trim(),
        name: value.name.trim(),
        description: value.description.trim() || undefined,
        lifecycleKind: value.lifecycleKind,
        catalogTypeCode: value.catalogTypeCode,
        validFrom: value.lifecycleKind === 'TEMPORARY' ? value.validFrom : undefined,
        validTo: value.lifecycleKind === 'TEMPORARY' ? value.validTo : undefined,
      },
      {
        onSuccess: () => {
          success(t('publicationCatalogsPanel.created'));
          setFormOpen(false);
        },
        onError: handleError,
      },
    );
  };

  const runLifecycleAction = () => {
    if (!confirmTarget) return;
    const { action, catalog } = confirmTarget;
    const onSuccessGeneric = () => {
      success(t(`publicationCatalogsPanel.lifecycle.${action}Success`, { name: catalog.name }));
      setConfirmTarget(null);
    };
    const onErrorGeneric = (error: NormalizedApiError) => {
      notifyError(error.message);
      setConfirmTarget(null);
    };
    switch (action) {
      case 'activate':
        activate.mutate(catalog.id, { onSuccess: onSuccessGeneric, onError: onErrorGeneric });
        return;
      case 'pause':
        pause.mutate(catalog.id, { onSuccess: onSuccessGeneric, onError: onErrorGeneric });
        return;
      case 'resume':
        resume.mutate(catalog.id, { onSuccess: onSuccessGeneric, onError: onErrorGeneric });
        return;
      case 'archive':
        archive.mutate(catalog.id, { onSuccess: onSuccessGeneric, onError: onErrorGeneric });
        return;
      case 'republish':
        republish.mutate(catalog.id, {
          onSuccess: (run) => {
            success(
              t('publicationCatalogsPanel.lifecycle.republishSuccess', {
                name: catalog.name,
                included: run.productsIncluded,
                excluded: run.productsExcluded,
              }),
            );
            setConfirmTarget(null);
          },
          onError: onErrorGeneric,
        });
        return;
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('publicationCatalogsPanel.description')}
        </Typography>
        {canManage ? (
          <Button
            startIcon={<AddRoundedIcon />}
            variant="outlined"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            {t('publicationCatalogsPanel.addCatalog')}
          </Button>
        ) : null}
      </Stack>

      {catalogTypesQuery.isError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('publicationCatalogsPanel.catalogTypesLoadError')}
        </Alert>
      ) : null}

      {catalogs.length === 0 ? (
        <EmptyState
          title={t('publicationCatalogsPanel.empty.title')}
          description={t('publicationCatalogsPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('publicationCatalogsPanel.table.catalog')}</TableCell>
                <TableCell>{t('publicationCatalogsPanel.table.type')}</TableCell>
                <TableCell>{t('publicationCatalogsPanel.table.lifecycleKind')}</TableCell>
                <TableCell>{t('publicationCatalogsPanel.table.status')}</TableCell>
                <TableCell>{t('publicationCatalogsPanel.table.validity')}</TableCell>
                <TableCell>{t('publicationCatalogsPanel.table.updated')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {catalogs.map((catalog) => (
                <TableRow
                  key={catalog.id}
                  hover
                  onClick={() => navigate(`/catalog/publication/catalogs/${catalog.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{catalog.name}</Typography>
                    <Typography variant="caption" color="text.secondary" component="code">
                      {catalog.code}
                    </Typography>
                  </TableCell>
                  <TableCell>{catalogTypeLabel(catalog.catalogTypeCode)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(PUBLICATION_LIFECYCLE_KIND_LABELS[catalog.lifecycleKind])}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={publicationCatalogStatusColor(catalog.status)}
                      label={t(PUBLICATION_CATALOG_STATUS_LABELS[catalog.status] ?? catalog.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {catalog.lifecycleKind === 'TEMPORARY' ? (
                      <Typography variant="body2" noWrap>
                        {formatDateTime(catalog.validFrom)} – {formatDateTime(catalog.validTo)}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('publicationCatalogsPanel.table.permanentValidity')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDateTime(catalog.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                    {canManage ? (
                      <IconButton
                        size="small"
                        aria-label={t('publicationCatalogsPanel.table.rowActionsAria', {
                          name: catalog.name,
                        })}
                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, catalog })}
                      >
                        <MoreVertRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) setEditing(menuAnchor.catalog);
            setFormOpen(true);
            setMenuAnchor(null);
          }}
        >
          {t('common:action.edit')}
        </MenuItem>
        {(['activate', 'pause', 'resume', 'republish', 'archive'] as LifecycleAction[]).map((action) => (
          <MenuItem
            key={action}
            disabled={!menuAnchor || !isLifecycleActionEnabled(menuAnchor.catalog, action)}
            onClick={() => {
              if (menuAnchor) setConfirmTarget({ action, catalog: menuAnchor.catalog });
              setMenuAnchor(null);
            }}
          >
            {t(`publicationCatalogsPanel.lifecycle.${action}Action`)}
          </MenuItem>
        ))}
      </Menu>

      <PublicationCatalogFormDialog
        open={formOpen}
        editing={editing}
        catalogTypes={catalogTypes}
        saving={create.isPending || update.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={submitForm}
      />

      <Dialog open={confirmTarget !== null} onClose={() => (!lifecyclePending ? setConfirmTarget(null) : undefined)}>
        <DialogTitle>
          {confirmTarget
            ? t(`publicationCatalogsPanel.lifecycle.${confirmTarget.action}ConfirmTitle`)
            : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmTarget
              ? t(`publicationCatalogsPanel.lifecycle.${confirmTarget.action}ConfirmBody`, {
                  name: confirmTarget.catalog.name,
                })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={lifecyclePending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            color={confirmTarget?.action === 'archive' ? 'error' : 'primary'}
            onClick={runLifecycleAction}
            disabled={lifecyclePending}
            startIcon={lifecyclePending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {confirmTarget ? t(`publicationCatalogsPanel.lifecycle.${confirmTarget.action}Action`) : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
